import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getSegments,
  createCampaign,
} from "../../../../services/communication.services";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const CampaignEditor = ({ fetchCampaigns, onClose, campaignName, channel }) => {
  const [from, setFrom] = useState("");
  const [segment, setSegment] = useState("");
  const [segments, setSegments] = useState([]);
  const [description, setDescription] = useState("Default Message Content");
  const [editingSection, setEditingSection] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  // Set default "from" based on channel
  useEffect(() => {
    if (channel === "Email") {
      setFrom("donotreply.aaraconsultancy@gmail.com");
    } else {
      setFrom("");
    }
  }, [channel]);

  // Fetch segments
  const fetchSegments = async () => {
    try {
      const response = await getSegments();
      setSegments(response?.segment_counts || []);
    } catch (error) {
      console.error(
        error.response?.data?.message || "Failed to fetch segments"
      );
    }
  };

  useEffect(() => {
    fetchSegments();
  }, [channel]);

  // Quill editor configuration for email
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ color: [] }, { background: [] }],
      [{ font: [] }],
      [{ align: [] }],
      ["link"],
      ["blockquote", "code-block"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "indent",
    "color",
    "background",
    "font",
    "align",
    "link",
    "blockquote",
    "code-block",
  ];

  const handleCreateCampaign = async () => {
    if (!segment || !description) {
      toast.error("Please fill in all the required fields.");
      return;
    }

    const campaignData = {
      name: campaignName,
      channel,
      from,
      segment,
      description,
    };

    try {
      setIsSubmitting(true);
      const response = await createCampaign(campaignData);
      toast.success("Campaign created successfully!");
      onClose();
      fetchCampaigns();
    } catch (error) {
      console.error(error.message || "Failed to create campaign");
      toast.error(error.message || "Failed to create campaign");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get placeholder text based on channel
  const getPlaceholderText = () => {
    if (channel === "Email") {
      return `Default message`;
    }
    return "Enter your message content here...";
  };

  // Set default content based on channel
  useEffect(() => {
    if (channel === "Email") {
      setDescription(getPlaceholderText());
    } else {
      setDescription("Default Message Content");
    }
  }, [channel]);

  return (
    <div
      className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-[1000] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white w-full max-w-sm sm:max-w-md lg:max-w-4xl xl:max-w-5xl rounded-lg shadow-lg relative max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg z-10">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800">
            Create a {channel} Campaign
          </h2>
          <button
            className="text-gray-500 hover:text-gray-800 text-xl p-1 hover:bg-gray-100 rounded-full transition-colors"
            onClick={onClose}
          >
            &times;
          </button>
        </div>

        <div className="p-4 sm:p-6">
          {/* Draft and Navigation */}
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <div className="flex items-center gap-2 sm:gap-4">
              <button
                className="text-gray-500 hover:text-gray-800 p-1 sm:p-2 hover:bg-gray-100 rounded transition-colors"
                onClick={onClose}
              >
                ←
              </button>
              <span className="text-gray-800 text-base sm:text-lg font-medium truncate">
                {campaignName}
              </span>
            </div>
          </div>

          {/* Campaign Steps */}
          <div className="space-y-4 sm:space-y-6">
            {/* From Section – only for Email */}
            {channel === "Email" && (
              <div className="bg-gray-50 p-3 sm:p-4 rounded-lg shadow-sm">
                <p className="text-gray-800 font-medium text-sm sm:text-base">
                  From
                </p>
                <p className="text-xs sm:text-sm text-gray-600 break-all">
                  donotreply.aaraconsultancy@gmail.com
                </p>
              </div>
            )}

            {/* To Section */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-gray-800 font-medium text-sm sm:text-base">
                    To
                  </p>
                  {editingSection === "segment" ? (
                    segments.length === 0 ? (
                      <p className="text-xs sm:text-sm text-gray-500">
                        No segments available
                      </p>
                    ) : (
                      <select
                        value={segment}
                        onChange={(e) => setSegment(e.target.value)}
                        className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:outline-none text-sm"
                      >
                        <option value="" disabled>
                          Select a Segment
                        </option>
                        {segments.map((segment, index) => (
                          <option
                            key={index}
                            value={
                              segment?.segment__name || `Segment ${index + 1}`
                            }
                          >
                            {segment?.segment__name || `Unnamed Segment`}
                          </option>
                        ))}
                      </select>
                    )
                  ) : (
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      {segment}
                    </p>
                  )}
                </div>
                <button
                  onClick={() =>
                    setEditingSection(
                      editingSection === "segment" ? null : "segment"
                    )
                  }
                  className="text-purple-600 text-xs sm:text-sm font-medium hover:underline whitespace-nowrap flex-shrink-0"
                >
                  {editingSection === "segment" ? "Save" : "Edit"}
                </button>
              </div>
            </div>

            {/* Message Content Section */}
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg shadow-sm">
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <p className="text-gray-800 font-medium text-sm sm:text-base">
                    Message content
                  </p>
                  <button
                    onClick={() =>
                      setEditingSection(
                        editingSection === "description" ? null : "description"
                      )
                    }
                    className="text-purple-600 text-xs sm:text-sm font-medium hover:underline whitespace-nowrap"
                  >
                    {editingSection === "description" ? "Save" : "Edit"}
                  </button>
                </div>

                <div className="w-full">
                  {editingSection === "description" ? (
                    <>
                      {channel === "Email" ? (
                        <div className="email-editor-container">
                          <ReactQuill
                            value={description}
                            onChange={setDescription}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder="Compose your email..."
                            className="bg-white"
                            style={{
                              height: "300px",
                              marginBottom: "50px",
                            }}
                          />

                          {/* Email Template Suggestions */}
                        </div>
                      ) : (
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          rows="4"
                          placeholder={getPlaceholderText()}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:outline-none text-sm resize-y"
                        />
                      )}
                    </>
                  ) : (
                    <div className="text-xs sm:text-sm text-gray-600 max-h-32 sm:max-h-40 overflow-y-auto w-full whitespace-pre-wrap border border-gray-200 rounded-md p-2 sm:p-3 bg-white">
                      {channel === "Email" ? (
                        <div
                          className="prose max-w-none"
                          dangerouslySetInnerHTML={{ __html: description }}
                        />
                      ) : (
                        description
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Email Preview Section - Only for Email campaigns */}
            {/* {channel === "Email" && editingSection !== "description" && (
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="p-3 sm:p-4 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-800 flex items-center">
                    📧 Email Preview
                  </h3>
                </div>
                <div className="p-4">
                  <div className="mb-3 text-xs text-gray-500">
                    <div>
                      <strong>From:</strong> donotreply.aaraconsultancy@gmail.com
                    </div>
                    <div>
                      <strong>To:</strong> {segment || "Selected Segment"}
                    </div>
                    <div>
                      <strong>Subject:</strong> {campaignName}
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <div
                      className="prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: description }}
                    />
                  </div>
                </div>
              </div>
            )} */}

            {/* Submit Button */}
            <div className="pt-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-medium border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCampaign}
                  className={`w-full sm:w-auto bg-purple-600 text-white px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center ${
                    isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Creating Campaign...
                    </>
                  ) : (
                    `Start Creating ${channel} Campaign`
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom CSS for Quill Editor */}
      <style jsx global>{`
        .email-editor-container .ql-editor {
          min-height: 200px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
            sans-serif;
          font-size: 14px;
          line-height: 1.6;
        }

        .email-editor-container .ql-toolbar {
          border-top: 1px solid #ccc;
          border-left: 1px solid #ccc;
          border-right: 1px solid #ccc;
          border-radius: 8px 8px 0 0;
        }

        .email-editor-container .ql-container {
          border-bottom: 1px solid #ccc;
          border-left: 1px solid #ccc;
          border-right: 1px solid #ccc;
          border-radius: 0 0 8px 8px;
        }

        .email-editor-container .ql-editor.ql-blank::before {
          font-style: normal;
          color: #9ca3af;
        }

        /* Mobile responsiveness */
        @media (max-width: 640px) {
          .email-editor-container .ql-toolbar {
            padding: 8px;
          }

          .email-editor-container .ql-formats {
            margin-right: 8px;
          }
        }
      `}</style>
    </div>
  );
};

export default CampaignEditor;
